<script lang="ts">
	import type { LightshowTrack } from "./lightshow-preview-track-types"
	import { Responsive } from "responsive"

	interface Props {
		tracks: LightshowTrack[]
		isPlaying: boolean
		currentTrackIndex: number
		jumpToTrack: (trackIndex: number) => void
	}

	let { tracks, isPlaying, currentTrackIndex, jumpToTrack }: Props = $props()
	let isMobile = $derived(Responsive.isMobile)
</script>

<div class="thumbnail-sidebar" class:isMobile>
	<div class="thumbnail-list">
		{#each tracks as track, index}
			{@const isCurrent = index === currentTrackIndex}
			<!-- svelte-ignore a11y_click_events_have_key_events -->
			<div
				class="thumbnail-item"
				class:active={isCurrent}
				class:playing={isPlaying && isCurrent}
				onclick={() => jumpToTrack(index)}
				role="button"
				tabindex="0"
			>
				{#if track.mediaType === "scene"}
					<div class="thumbnail-wrapper">
						{#if track.thumbnail}
							<img src={track.thumbnail} alt={track.name} class="thumbnail-image" />
						{:else}
							<div class="thumbnail-placeholder">
								<span>Thumbnail</span>
							</div>
						{/if}
						<div class="thumbnail-overlay">
							<span class="track-number">{index + 1}</span>
						</div>
					</div>
					<div class="thumbnail-label">{track.name}</div>
				{:else}
					<div class="thumbnail-wrapper break-thumbnail">
						<div class="break-thumbnail-content">
							<span class="break-duration">{track.duration} sec break</span>
						</div>
						<div class="thumbnail-overlay">
							<span class="track-number">{index + 1}</span>
						</div>
					</div>
					<div class="thumbnail-label">{track.name}</div>
				{/if}
			</div>
		{/each}
	</div>
</div>

<style>
	/* Thumbnail Sidebar */
	.thumbnail-sidebar {
		width: 20%;
		min-width: 12rem;
		position: absolute;
		top: 3.5rem;
		right: 0;
		bottom: 0;
		overflow-y: auto;
		padding: 0.4rem;
	}

	.thumbnail-sidebar.isMobile {
		width: 100%;
		min-width: unset;
		top: unset;
		right: unset;
		left: 0;
		bottom: 0;
		height: 8rem;
		overflow-y: hidden;
		overflow-x: auto;
		padding: 0.5rem;
		pointer-events: none;
	}

	.thumbnail-list {
		display: flex;
		flex-direction: column;
		gap: 0.4rem;
	}

	.thumbnail-sidebar.isMobile .thumbnail-list {
		flex-direction: row;
		gap: 0.5rem;
		pointer-events: auto;
	}

	.thumbnail-item {
		position: relative;
		flex: 0 0 auto;
		cursor: pointer;
		transition: all 0.2s ease;
		border-radius: var(--br);
		overflow: hidden;
		width: 100%;
		border: 2px solid transparent;
	}

	.thumbnail-sidebar.isMobile .thumbnail-item {
		width: 120px;
		height: 67px;
	}

	.thumbnail-item:hover {
		box-shadow: var(--shadow-medium);
	}

	.thumbnail-item.active {
		border-color: var(--color-main);
		box-shadow: var(--shadow-large);
	}

	.thumbnail-item.active.playing {
		animation: pulse 2s infinite;
	}

	.thumbnail-wrapper {
		position: relative;
		width: 100%;
		height: 0;
		padding-bottom: 56.25%; /* 16:9 aspect ratio */
	}

	.thumbnail-image {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		object-fit: cover;
	}

	.thumbnail-placeholder {
		width: 100%;
		height: 100%;
		display: flex;
		align-items: center;
		justify-content: center;
		background: var(--color-bg);
		color: var(--color-text);
	}

	.break-thumbnail {
		background: var(--color-bg);
	}

	.break-thumbnail-content {
		width: 100%;
		height: 100%;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		padding-top: 1rem;
		color: var(--color-text);
	}

	.break-duration {
		font-size: 0.8rem;
		opacity: 0.8;
	}

	.thumbnail-overlay {
		position: absolute;
		top: 0;
		left: 0;
		line-height: 1;
		border-bottom-right-radius: var(--br);
		background-color: var(--color-bg-darker);
		padding: 0.2rem 0.5rem 0.4rem 0.5rem;
	}

	.thumbnail-label {
		position: absolute;
		bottom: 0;
		padding: 0.25rem 0.25rem;
		background: var(--color-bg);
		left: 0;
		right: 0;
		font-size: var(--text-small);
		color: var(--color-text-light);
		text-align: center;
		line-height: 1.2;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	@keyframes pulse {
		0% {
			box-shadow: 0 0 0 0 rgba(var(--color-main-rgb), 0.4);
		}
		70% {
			box-shadow: 0 0 0 6px rgba(var(--color-main-rgb), 0);
		}
		100% {
			box-shadow: 0 0 0 0 rgba(var(--color-main-rgb), 0);
		}
	}
</style>
